import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiPatch, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import {
  Users, UserPlus, Mail, Check, X, Trash2, Shield, Copy, AlertTriangle,
  Clock, ShieldCheck, CheckCircle2, UserCheck, Sparkles, MessageCircle, RefreshCw, Bot,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fmtFromNow } from '../lib/format.js';
import { getSocket } from '../lib/socket.js';
import PendingSyncRequestsCard from '../components/team/PendingSyncRequestsCard.jsx';

export { PendingSyncRequestsCard };

const STATUS_COLORS = {
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  pending_approval: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  accepted: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  expired: 'bg-white/10 text-white/50 border-white/20',
};

export default function Team() {
  const qc = useQueryClient();
  const { data: invData } = useQuery({ queryKey: ['team-invites'], queryFn: () => apiGet('/team/invites') });
  const { data: pendingData } = useQuery({ queryKey: ['pending-teammate-approvals'], queryFn: () => apiGet('/team/pending-approvals') });
  const { data: syncReqData } = useQuery({ queryKey: ['team-sync-requests'], queryFn: () => apiGet('/sync/pending'), refetchInterval: 10000 });
  const { data: integrationsData } = useQuery({ queryKey: ['integrations'], queryFn: () => apiGet('/settings/integrations') });
  const emailProviderMissing = integrationsData?.integrations?.sendgrid?.stub === true;
  const { data: setData } = useQuery({ queryKey: ['team-settings'], queryFn: () => apiGet('/team/settings') });
  const { data: agentData } = useQuery({ queryKey: ['agents'], queryFn: () => apiGet('/agents') });

  const invites = invData?.items || [];
  const pendingApprovals = pendingData?.items || pendingData?.pendingAgents || [];
  const pendingSyncRequests = syncReqData?.requests || [];
  const settings = setData?.settings || {};
  const agents = agentData?.agents || agentData?.items || [];

  // Real-time listener for agent registration notifications
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    function handleAgentRegistered(payload) {
      const name = payload?.agent?.name || payload?.agent?.email || 'A teammate';
      toast(`🔔 ${name} registered and is pending your approval!`, {
        duration: 6000,
      });
      qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
      qc.invalidateQueries({ queryKey: ['team-invites'] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    }

    function handleSyncRequested(payload) {
      const name = payload?.requester?.name || 'A teammate';
      toast(`🔔 ${name} sent a template sync request!`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['team-sync-requests'] });
    }

    function refreshPending() {
      qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
      qc.invalidateQueries({ queryKey: ['team-invites'] });
    }

    socket.on('team:agent-registered', handleAgentRegistered);
    socket.on('team:pending_count_updated', refreshPending);
    socket.on('sync:requested', handleSyncRequested);
    return () => {
      socket.off('team:agent-registered', handleAgentRegistered);
      socket.off('team:pending_count_updated', refreshPending);
      socket.off('sync:requested', handleSyncRequested);
    };
  }, [qc]);

  return (
    <div className="space-y-6">
      <TopBar
        title="My Team"
        subtitle="Invite agents to your sub-team · Approve teammates before line setup · Manage agency team settings"
      />

      {/* Pending Teammate Approvals Safety Gate Card */}
      <PendingApprovalsCard
        items={pendingApprovals}
        onApproved={() => {
          qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
          qc.invalidateQueries({ queryKey: ['team-invites'] });
          qc.invalidateQueries({ queryKey: ['agents'] });
        }}
      />

      {/* Incoming Template Sync Requests Approval Hub Card */}
      <PendingSyncRequestsCard
        items={pendingSyncRequests}
        onApproved={() => {
          qc.invalidateQueries({ queryKey: ['team-sync-requests'] });
        }}
      />

      {/* Invite Agent / Add Teammate Form */}
      <NewInviteForm onCreated={() => qc.invalidateQueries({ queryKey: ['team-invites'] })} />

      <AbsorbAgentForm onAbsorbed={() => qc.invalidateQueries({ queryKey: ['agents'] })} />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel p-0 overflow-hidden">
          {emailProviderMissing && (
            <div className="px-4 py-3 border-b border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">Email provider simulated.</span> Use the &ldquo;Copy Invite Link&rdquo; button on each invite to dispatch manually via WhatsApp or SMS.
              </div>
            </div>
          )}
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between text-sm text-white/70">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> Sent Invitations
            </div>
            <span className="text-xs text-white/40">{invites.length} total</span>
          </div>
          <div className="divide-y divide-white/5">
            {invites.length === 0 && <div className="p-6 text-white/50 text-sm">No invites yet. Add a teammate above.</div>}
            {invites.map((inv) => {
              const inviteLink = inv.token
                ? `${window.location.origin}/signup?ref=${inv.inviterId || inv.invitedBy || ''}&token=${inv.token}`
                : null;
              return (
                <div key={inv._id} className="p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium text-white truncate">{inv.email}</div>
                    <div className="text-white/50 text-xs">
                      {inv.name ? `${inv.name} · ` : ''}role: {inv.role} · invited {new Date(inv.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-sm border text-xs font-mono uppercase tracking-wider ${STATUS_COLORS[inv.status] || ''}`}>
                      {inv.status}
                    </span>
                    {inv.status === 'pending' && inviteLink && (
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(inviteLink);
                            toast.success('Invite link copied for WhatsApp/SMS');
                          } catch {
                            toast.error('Copy failed — select manually');
                          }
                        }}
                        title={inviteLink}
                        className="px-2.5 py-1 rounded-md bg-white/10 text-white hover:bg-white/20 text-xs flex items-center gap-1.5 transition"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy Invite Link
                      </button>
                    )}
                    {inv.status === 'accepted' && (
                      <>
                        <button
                          onClick={async () => {
                            await apiPost('/team/approve-teammate', { inviteId: inv._id });
                            toast.success('Teammate approved!');
                            qc.invalidateQueries({ queryKey: ['team-invites'] });
                            qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
                            qc.invalidateQueries({ queryKey: ['agents'] });
                          }}
                          className="px-2.5 py-1 rounded-md bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-xs flex items-center gap-1 font-semibold transition"
                        >
                          <Check className="w-3.5 h-3.5" /> Approve Teammate
                        </button>
                        <button
                          onClick={async () => {
                            await apiPost('/team/reject-teammate', { inviteId: inv._id });
                            toast('Rejected');
                            qc.invalidateQueries({ queryKey: ['team-invites'] });
                            qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
                          }}
                          className="px-2.5 py-1 rounded-md bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-xs flex items-center gap-1 transition"
                        >
                          <X className="w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={async () => {
                        if (confirm('Delete this invite?')) {
                          await apiDel(`/team/invites/${inv._id}`);
                          qc.invalidateQueries({ queryKey: ['team-invites'] });
                        }
                      }}
                      className="text-white/40 hover:text-rose-400 p-1"
                      title="Delete invite"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <TeamSettings settings={settings} onSaved={() => qc.invalidateQueries({ queryKey: ['team-settings'] })} />
      </div>

      {/* Active Agents Table */}
      <div className="panel p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between text-sm text-white/70">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" /> Active Agents ({agents.length})
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-white/50 text-xs border-b border-white/5 bg-white/2">
            <tr>
              <th className="px-4 py-2.5">Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Wallet Balance</th>
              <th>Lead Assist</th>
              <th>Available</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {agents.map((a) => (
              <tr key={a._id} className="hover:bg-white/2 transition">
                <td className="px-4 py-3 font-medium text-white">{a.name}</td>
                <td className="text-white/70">{a.email}</td>
                <td className="text-white/70 capitalize">{a.role}</td>
                <td>
                  <span className={`px-2 py-0.5 rounded-sm border text-[11px] font-mono uppercase ${STATUS_COLORS[a.status] || STATUS_COLORS.active}`}>
                    {a.status || (a.active ? 'active' : 'disabled')}
                  </span>
                </td>
                <td className="font-mono text-emerald-400">
                  ${(a.walletBalance || 0).toFixed(2)}
                </td>
                <td>
                  <LeadAssistCell agent={a} onChanged={() => qc.invalidateQueries({ queryKey: ['agents'] })} />
                </td>
                <td>
                  {a.available ? (
                    <span className="text-emerald-400 text-xs font-semibold flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span> Available
                    </span>
                  ) : (
                    <span className="text-white/30 text-xs flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-white/30"></span> Offline
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Pending Teammate Approvals Safety Gate Card ──────────────────────────────
export function PendingApprovalsCard({ items = [], onApproved }) {
  const [busyId, setBusyId] = useState(null);

  if (items.length === 0) return null;

  async function handleApprove(agentId) {
    setBusyId(agentId);
    try {
      const res = await apiPost('/team/approve-teammate', { agentId });
      if (res?.ok) {
        toast.success('Teammate approved! Their account is now active.');
        onApproved?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Approval failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(agentId) {
    if (!confirm('Reject this teammate registration?')) return;
    setBusyId(agentId);
    try {
      const res = await apiPost('/team/reject-teammate', { agentId });
      if (res?.ok) {
        toast('Teammate registration rejected');
        onApproved?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Rejection failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="panel p-0 overflow-hidden border border-amber-500/30 bg-amber-500/5 shadow-xl">
      <div className="px-5 py-3.5 bg-amber-500/15 border-b border-amber-500/20 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Clock className="w-5 h-5 text-amber-400 animate-pulse" />
          <h3 className="font-display font-bold text-white text-base">
            Pending Teammate Approvals
          </h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-400 text-black">
            {items.length}
          </span>
        </div>
        <span className="text-xs text-amber-200/80 hidden sm:inline">
          Agents must be approved before purchasing numbers or dialing
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/50 text-xs border-b border-white/5 bg-black/20">
            <tr>
              <th className="px-5 py-3">Invitee Name</th>
              <th>Registered Email</th>
              <th>Registration Timestamp</th>
              <th className="text-right px-5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((agent) => (
              <tr key={agent._id} className="hover:bg-white/2 transition">
                <td className="px-5 py-3.5 font-medium text-white flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-amber-400" />
                  {agent.name}
                </td>
                <td className="text-white/80 font-mono text-xs">{agent.email}</td>
                <td className="text-white/50 text-xs">
                  {agent.createdAt ? new Date(agent.createdAt).toLocaleString() : 'Just now'}
                </td>
                <td className="px-5 py-3.5 text-right space-x-2 whitespace-nowrap">
                  <button
                    onClick={() => handleApprove(agent._id)}
                    disabled={busyId === agent._id}
                    className="px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition shadow-md inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Check className="w-3.5 h-3.5" /> Approve Teammate
                  </button>
                  <button
                    onClick={() => handleReject(agent._id)}
                    disabled={busyId === agent._id}
                    className="px-3 py-1.5 rounded-md bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 text-xs font-medium transition inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Add Teammate / Invite Agent Form ─────────────────────────────────────────
function NewInviteForm({ onCreated }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLeadAssist, setIsLeadAssist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return toast.error('Email required');
    setBusy(true);
    try {
      const r = await apiPost('/team/invites', {
        email: email.trim(),
        name: name.trim() || undefined,
        role: 'agent',
        isLeadAssist,
      });

      const inviteUrl = r.signupLink || r.link || (r.invite?.token ? `${window.location.origin}/signup?ref=${r.invite.inviterId || ''}&token=${r.invite.token}` : '');
      setLastInviteLink(inviteUrl);

      if (inviteUrl) {
        navigator.clipboard.writeText(inviteUrl).catch(() => {});
        toast.success('Teammate invited! Invite link copied to clipboard.', { duration: 5000 });
      } else {
        toast.success('Teammate invite dispatched!');
      }

      setEmail('');
      setName('');
      setIsLeadAssist(false);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Invite failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="panel p-5 space-y-4 border border-civix-purple/20 bg-gradient-to-r from-civix-panel to-civix-panelLight">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold text-white text-base flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-civix-cyan" /> Add Teammate / Invite Agent
          </h3>
          <p className="text-xs text-white/50 mt-0.5">
            Everyone on CIVIX is an agent and the admin of their own account. Add a teammate by email and they'll get their own login.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-semibold text-white/70 mb-1">Full Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            className="input w-full"
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-semibold text-white/70 mb-1">Email Address</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="agent@example.com"
            className="input w-full"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 font-semibold text-sm shadow-glow-purple disabled:opacity-50"
        >
          <UserPlus className="w-4 h-4" /> {busy ? 'Inviting…' : 'Invite Agent'}
        </button>
      </div>

      {lastInviteLink && (
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between gap-3 text-xs">
          <div className="truncate text-emerald-200">
            <strong>Link:</strong> <span className="font-mono text-[11px]">{lastInviteLink}</span>
          </div>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(lastInviteLink);
              toast.success('Invite link copied for WhatsApp/SMS!');
            }}
            className="px-3 py-1 rounded bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition flex items-center gap-1.5 shrink-0"
          >
            <Copy className="w-3.5 h-3.5" /> Copy Invite Link (WhatsApp/SMS)
          </button>
        </div>
      )}

      <label className="flex items-start gap-3 p-3 rounded-md border border-white/5 hover:border-white/15 cursor-pointer bg-black/20">
        <input
          type="checkbox"
          checked={isLeadAssist}
          onChange={(e) => setIsLeadAssist(e.target.checked)}
          className="mt-1 accent-civix-purple"
        />
        <div className="flex-1">
          <div className="text-sm font-medium text-white">Lead Assist Mode</div>
          <div className="text-xs text-white/50">
            Lead Assist: turn this on for someone who dials your shared Lead Pool on your behalf — their calls, texts, and AI usage are billed to you, and they won't be assigned their own leads. Turn it off when they become a full contractor, and they'll start paying for their own usage.
          </div>
        </div>
      </label>
    </form>
  );
}

function LeadAssistCell({ agent, onChanged }) {
  const [on, setOn] = useState(!!agent.isLeadAssist);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    const next = !on;
    setBusy(true);
    setOn(next);
    try {
      await apiPatch(`/agents/${agent._id}/lead-assist`, { isLeadAssist: next });
      toast.success(next ? 'Lead Assist on' : 'Lead Assist off');
      onChanged?.();
    } catch (err) {
      setOn(!next);
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={toggle}
      disabled={busy}
      title={on ? 'Billed to agency · dials shared Lead Pool' : 'Self-funded · pays their own carrier usage'}
      className={`px-2 py-0.5 rounded border text-xs font-semibold ${
        on
          ? 'bg-civix-cyan/15 text-civix-cyan border-civix-cyan/30'
          : 'bg-white/10 text-white/50 border-white/20'
      }`}
    >
      {on ? 'On' : 'Off'}
    </button>
  );
}

function AbsorbAgentForm({ onAbsorbed }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr) return toast.error('Email required');
    if (!confirm(`Move ${addr} into your organization? They will inherit your A2P and become an agent on your team.`)) return;
    setBusy(true);
    try {
      await apiPost('/team/agents/absorb', { email: addr });
      toast.success(`${addr} moved into your organization`);
      setEmail('');
      onAbsorbed?.();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="panel p-4 space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-white/60 mb-1">Absorb existing self-signup agent into organization</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="existing-agent@example.com"
            className="input w-full text-xs"
          />
        </div>
        <button disabled={busy} className="btn-ghost text-xs inline-flex items-center gap-1.5 border border-white/10">
          <UserPlus className="w-3.5 h-3.5" /> Absorb agent
        </button>
      </div>
    </form>
  );
}

function TeamSettings({ settings, onSaved }) {
  const [s, setS] = useState({
    recordAllCalls: !!settings.recordAllCalls,
    monitorSoldLeads: !!settings.monitorSoldLeads,
    requireAdminApproval: settings.requireAdminApproval !== false,
    allowAgentSelfInvite: !!settings.allowAgentSelfInvite,
  });

  useEffect(() => {
    setS({
      recordAllCalls: !!settings.recordAllCalls,
      monitorSoldLeads: !!settings.monitorSoldLeads,
      requireAdminApproval: settings.requireAdminApproval !== false,
      allowAgentSelfInvite: !!settings.allowAgentSelfInvite,
    });
  }, [settings]);

  async function save() {
    try {
      await apiPut('/team/settings', s);
      toast.success('Team settings saved');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    }
  }

  const Toggle = ({ k, label, desc }) => (
    <label className="flex items-start gap-3 p-3 rounded-md border border-white/5 hover:border-white/15 cursor-pointer">
      <input
        type="checkbox"
        checked={s[k]}
        onChange={(e) => setS({ ...s, [k]: e.target.checked })}
        className="mt-1 accent-civix-purple"
      />
      <div className="flex-1">
        <div className="text-sm font-medium text-white">{label}</div>
        <div className="text-xs text-white/50">{desc}</div>
      </div>
    </label>
  );

  return (
    <div className="panel p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm text-white/70">
        <Shield className="w-4 h-4" /> Team settings
      </div>
      <Toggle k="recordAllCalls" label="Record all calls" desc="Auto-record every outbound + inbound call" />
      <Toggle k="monitorSoldLeads" label="Monitor sold leads" desc="Notify admins when a lead is marked SOLD" />
      <Toggle k="requireAdminApproval" label="Require manager approval" desc="New teammates are held in pending approval until approved" />
      <Toggle k="allowAgentSelfInvite" label="Allow teammate invites" desc="Let agents invite sub-team members" />
      <button onClick={save} className="btn-primary w-full text-xs font-semibold py-2">
        Save settings
      </button>
    </div>
  );
}
